import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { 
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  BellAlertIcon,
  CheckIcon,
  ClockIcon,
  PencilIcon,
  XMarkIcon,
  PlusIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import JsonViewer from '../../components/JsonViewer';
import NotFound from '../../components/NotFound';
import AttachmentTab from '../../components/AttachmentTab';
import RichTextDisplay from '../../components/RichTextDisplay';
import RecordTimeModal from '../../components/RecordTimeModal';
import { useAuth } from '../../contexts/AuthContext';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { stripHtml } from '../../utils/sanitize';
import { trackAlertOperation } from '../../utils/appInsights';

const severityIcons = {
  critical: <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />,
  major: <ExclamationCircleIcon className="h-6 w-6 text-orange-600" />,
  minor: <ExclamationCircleIcon className="h-6 w-6 text-yellow-600" />,
  warning: <BellAlertIcon className="h-6 w-6 text-blue-600" />,
  info: <InformationCircleIcon className="h-6 w-6 text-gray-600" />
};

const severityColors = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  major: 'bg-orange-100 text-orange-800 border-orange-200',
  minor: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  warning: 'bg-blue-100 text-blue-800 border-blue-200',
  info: 'bg-gray-100 text-gray-800 border-gray-200'
};

const stateColors = {
  new: 'bg-blue-100 text-blue-800',
  acknowledged: 'bg-yellow-100 text-yellow-800',
  assigned: 'bg-purple-100 text-purple-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  resolved: 'bg-green-100 text-green-800',
  auto_resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
  open: 'bg-blue-100 text-blue-800'
};

const workflowSteps = [
  { id: 'open', name: 'Open', icon: BellAlertIcon },
  { id: 'new', name: 'New', icon: BellAlertIcon },
  { id: 'acknowledged', name: 'Acknowledged', icon: ClockIcon },
  { id: 'assigned', name: 'Assigned', icon: PencilIcon },
  { id: 'in_progress', name: 'In Progress', icon: ClockIcon },
  { id: 'resolved', name: 'Resolved', icon: CheckIcon },
  { id: 'auto_resolved', name: 'Auto-Resolved', icon: CheckIcon },
  { id: 'closed', name: 'Closed', icon: XMarkIcon }
];

interface AlertEditForm {
  shortDescription?: string;
  description?: string;
  severity?: string;
  source?: string;
  node?: string;
  metric?: string;
  category?: string;
  state?: string;
}

const AlertDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formatDateTime } = useDateFormatter();
  const navigate = useNavigate();
  const location = useLocation();
  const alertsListSearch = (location.state as { alertsListSearch?: string } | null)?.alertsListSearch;
  const alertsListPath =
    alertsListSearch !== undefined ? `/alerts${alertsListSearch}` : '/alerts';
  const navigateBackToAlerts = () => navigate(alertsListPath);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const hasLoggedView = useRef(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<AlertEditForm>({});
  const [errors, setErrors] = useState<any>({});
  const [activeTab, setActiveTab] = useState<'details' | 'attachments'>('details');
  const [recordTimeModalOpen, setRecordTimeModalOpen] = useState(false);

  // Suppress unused variable warning - errors is used in updateAlertMutation
  void errors;

  const { data: alert, isLoading, error } = useQuery({
    queryKey: ['alert', id],
    queryFn: async () => {
      const response = await api.get(`/alerts/${id}`);
      return response.data;
    }
  });

  // Log "Viewed Alert Details" when alert is loaded
  useEffect(() => {
    if (alert && user && !hasLoggedView.current && !isLoading) {
      hasLoggedView.current = true;
      trackAlertOperation(
        'Alert Details',
        user.email || user.firstName || 'Unknown',
        {
          action: 'Viewed Alert Details',
          alertNumber: alert.number || alert.id || id || 'Unknown',
        },
        {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
        }
      );
    }
  }, [alert, user, isLoading, id, isServiceDeskUser]);

  const { data: resourceData } = useQuery({
    queryKey: ['resource', alert?.resourceId],
    queryFn: async () => {
      if (!alert?.resourceId) return null;
      try {
        const response = await api.get(`/resources/${alert.resourceId}`);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch resource:', error);
        return null;
      }
    },
    enabled: !!alert?.resourceId
  });

  // Fetch primary contact for case creation
  const { data: primaryContactData } = useQuery({
    queryKey: ['primary-contact', alert?.customerId, alert?.engagementId],
    queryFn: async () => {
      const customerId = alert?.customerId || alert?.customer?.id;
      if (!customerId) return { contact: null };
      const params = new URLSearchParams({ customerId });
      const engagementId = alert?.engagementId || alert?.engagement?.id;
      if (engagementId) {
        params.append('engagementId', engagementId);
      }
      const response = await api.get(`/contacts/primary?${params.toString()}`);
      return response.data;
    },
    enabled: !!(alert?.customerId || alert?.customer?.id)
  });

  const acknowledgeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/alerts/${id}/acknowledge`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert', id] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    }
  });


  const updateStateMutation = useMutation({
    mutationFn: async (newState: string) => {
      const response = await api.patch(`/alerts/${id}`, { state: newState });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert', id] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    }
  });

  const createCaseMutation = useMutation({
    mutationFn: async (caseData: any) => {
      const response = await api.post('/cases', caseData);
      return response.data;
    },
    onSuccess: async (caseData) => {
      // The backend already links the alert to the case, so we just need to refresh the UI
      try {
        const caseId = caseData.id || caseData._id;
        // Invalidate alert queries to refresh the UI
        queryClient.invalidateQueries({ queryKey: ['alert', id] });
        queryClient.invalidateQueries({ queryKey: ['alerts'] });
        // Invalidate case queries to ensure fresh data with ReferenceAlert is loaded
        queryClient.invalidateQueries({ queryKey: ['case', caseId] });
        queryClient.invalidateQueries({ queryKey: ['cases'] });
      } catch (error) {
        console.error('Failed to refresh data:', error);
      }

      // Navigate to the newly created case
      navigate(`/cases/${caseData.id || caseData._id}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create case from alert';
      alert(message);
    }
  });

  const updateAlertMutation = useMutation({
    mutationFn: async (data: any) => {
      // Use PUT endpoint if it exists, otherwise use PATCH for limited updates
      const response = await api.put(`/alerts/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate both the individual alert and the alerts list
      queryClient.invalidateQueries({ queryKey: ['alert', id] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      // Also invalidate any filtered queries
      queryClient.invalidateQueries({ queryKey: ['alerts'], exact: false });
      setIsEditing(false);
      setErrors({});
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update alert';
      setErrors({ submit: message });
    }
  });

  const handleEdit = () => {
    // Normalize severity value - handle both old (HIGH/MEDIUM/LOW) and new (critical/major/minor/warning/info) formats
    let normalizedSeverity = alert.severity?.toLowerCase();

    // Map old severity values to new values
    const severityMap: Record<string, string> = {
      'high': 'critical',
      'medium': 'minor',
      'low': 'info'
    };

    if (normalizedSeverity && severityMap[normalizedSeverity]) {
      normalizedSeverity = severityMap[normalizedSeverity];
    }

    setEditForm({
      shortDescription: alert.shortDescription,
      description: alert.description,
      severity: normalizedSeverity || 'info',
      source: alert.source,
      node: alert.node,
      metric: alert.metric,
      category: alert.category,
      state: alert.state
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    const payload = { ...editForm };
    // Priority is automatically calculated from severity by the backend
    updateAlertMutation.mutate(payload);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({});
    setErrors({});
  };

  const createCaseFromAlert = () => {
    // Map alert severity to case priority enum values
    const severityToPriority: Record<string, string> = {
      'critical': 'critical_1',
      'major': 'high_2',
      'minor': 'moderate_3',
      'warning': 'low_4',
      'info': 'planning_5'
    };

    // Map alert priority if it's a string enum to case priority
    const alertPriorityToCasePriority: Record<string, string> = {
      'CRITICAL': 'critical_1',
      'HIGH': 'high_2',
      'NORMAL': 'moderate_3',
      'MEDIUM': 'low_4',
      'LOW': 'planning_5'
    };

    // Determine the priority - use severity mapping first, then alert priority
    let priority = alert.severity ? severityToPriority[alert.severity] : undefined;
    if (!priority && typeof alert.priority === 'string') {
      priority = alertPriorityToCasePriority[alert.priority];
    }
    if (!priority && typeof alert.priority === 'number') {
      // Map numeric priority to case priority enum
      const numericToPriority: Record<number, string> = {
        1: 'critical_1',
        2: 'high_2',
        3: 'moderate_3',
        4: 'low_4',
        5: 'planning_5'
      };
      priority = numericToPriority[alert.priority];
    }
    // Default to moderate if still no priority
    priority = priority || 'moderate_3';

    // Determine impact and urgency - handle missing severity gracefully
    const severity = alert.severity || 'low';
    const impact = severity === 'critical' ? 'high_1' :
                   severity === 'major' ? 'medium_2' : 'low_3';
    const urgency = severity === 'critical' ? 'high_1' :
                    severity === 'major' ? 'medium_2' : 'low_3';

    const caseData = {
      shortDescription: alert.shortDescription,
      description: alert.description || alert.shortDescription,
      priority: priority,
      category: 'issue',
      caseType: 'incident',
      channel: 'alert',
      impact: impact,
      urgency: urgency,
      alertId: alert.id, // Link the alert to the case
      caller: alert.caller?.id || alert.caller?._id || alert.callerId,
      customerId: alert.customerId || alert.customer?.id,
      engagementId: alert.engagementId || alert.engagement?.id,
      contactId: primaryContactData?.contact?.id, // Auto-select primary contact
      resourceId: alert.resourceId || alert.resource?.id,
      slaPlan: severity === 'critical' ? 'premium' : severity === 'major' ? 'standard' : 'basic'
    };

    createCaseMutation.mutate(caseData);
  };

  const getWorkflowStatus = (state: string) => {
    const stateIndex = workflowSteps.findIndex(step => step.id === state);
    return stateIndex;
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !alert) {
    return (
      <NotFound
        title="Alert Not Found"
        message="The alert you're looking for doesn't exist or may have been removed."
        backPath={alertsListPath}
        backLabel="View All Alerts"
      />
    );
  }

  const currentStepIndex = getWorkflowStatus(alert.state);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={navigateBackToAlerts}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="mr-1 h-4 w-4" />
          Back to Alerts
        </button>

        <div className="flex gap-2">
          {/* Record Time Button - Show when state is not new/closed/cancelled */}
          {alert &&
           user &&
           alert.state !== 'new' &&
           alert.state !== 'closed' &&
           alert.state !== 'cancelled' &&
           alert.customer &&
           alert.engagement && (
            <button
              onClick={() => setRecordTimeModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ClockIcon className="h-5 w-5 mr-2" />
              Record Time
            </button>
          )}

          {/* Related Case/Ticket link - shows when this alert has created a case */}
          {alert.relatedCaseId && alert.Case && (() => {
          const ticketType = alert.Case.ticketType || 'CASE';

          // Map ticket types to routes and labels
          const ticketTypeMap: { [key: string]: { route: string; label: string } } = {
            'INCIDENT': { route: 'incidents', label: 'Incident' },
            'PROBLEM': { route: 'problems', label: 'Problem' },
            'CHANGE': { route: 'changes', label: 'Change' },
            'CASE': { route: 'cases', label: 'Case' },
            'SERVICE_REQUEST': { route: 'service-requests', label: 'Service Request' }
          };

          const config = ticketTypeMap[ticketType] || { route: 'cases', label: 'Case' };

          return (
            <Link
              to={`/${config.route}/${alert.relatedCaseId}`}
              className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm font-medium"
            >
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 2 0 01-2 2z" />
              </svg>
              View Related {config.label} {alert.Case.number}
            </Link>
          );
        })()}
        </div>
      </div>

      {/* Notification for resolved alerts */}
      {(alert.state === 'resolved' || alert.state === 'auto_resolved') && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <CheckIcon className="h-5 w-5 text-green-400 mr-2" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-green-800">
                {alert.state === 'auto_resolved' ? 'Alert Auto-Resolved' : 'Alert Resolved'}
              </h3>
              <p className="mt-1 text-sm text-green-700">
                This alert has been {alert.state === 'auto_resolved' ? 'automatically resolved' : 'resolved'}.
                {alert.relatedCase && (
                  <> Case <Link 
                    to={`/cases/${alert.relatedCase.id || alert.relatedCase._id}`}
                    className="font-medium text-green-800 underline hover:text-green-900"
                  >
                    {alert.relatedCase.number}
                  </Link> has been created to track the investigation and resolution.</>
                )}
                {!alert.relatedCase && ' A case may have been created to track the investigation and resolution.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{alert.number}</h1>
              {isEditing ? (
                <div className="mt-2">
                  <input
                    type="text"
                    value={editForm.shortDescription || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, shortDescription: e.target.value }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Short description"
                  />
                </div>
              ) : (
                <p className="mt-1 text-lg text-gray-600">{stripHtml(alert.shortDescription)}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {isEditing ? (
                <>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Severity:</span>
                    <select
                      value={editForm.severity || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, severity: e.target.value }))}
                      className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="critical">Critical</option>
                      <option value="major">Major</option>
                      <option value="minor">Minor</option>
                      <option value="warning">Warning</option>
                      <option value="info">Info</option>
                    </select>
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={updateAlertMutation.isPending}
                    className="inline-flex items-center px-3 py-1 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    {updateAlertMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  {severityIcons[alert.severity as keyof typeof severityIcons]}
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${severityColors[alert.severity as keyof typeof severityColors]}`}>
                    {alert.severity.toUpperCase()}
                  </span>
                  {alert.state !== 'resolved' && alert.state !== 'auto_resolved' && alert.state !== 'closed' ? (
                    <button
                      onClick={() => handleEdit()}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                  ) : (
                    <div className="group relative">
                      <span className="inline-flex items-center px-3 py-1 border border-gray-200 rounded-md text-sm font-medium text-gray-400 bg-gray-50 cursor-not-allowed">
                        <LockClosedIcon className="h-4 w-4 mr-1" />
                        Locked
                      </span>
                      <div className="opacity-0 group-hover:opacity-100 absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-sm text-white bg-gray-900 rounded-md whitespace-nowrap transition-opacity duration-300 pointer-events-none">
                        Alert is {alert.state} and cannot be edited
                        {alert.relatedCaseId && <div className="text-xs">Case created from this alert</div>}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Workflow Progress */}
        <div className="px-6 py-4">
          <nav aria-label="Progress">
            <ol className="flex items-center">
              {workflowSteps.map((step, stepIdx) => {
                const Icon = step.icon;
                const isActive = stepIdx === currentStepIndex;
                const isCompleted = stepIdx < currentStepIndex;
                
                return (
                  <li key={step.id} className={stepIdx !== workflowSteps.length - 1 ? 'flex-1' : ''}>
                    <div className="flex items-center">
                      <div className={`
                        flex items-center justify-center w-10 h-10 rounded-full
                        ${isCompleted ? 'bg-indigo-600' : isActive ? 'bg-indigo-600' : 'bg-gray-300'}
                      `}>
                        <Icon className={`w-6 h-6 ${isCompleted || isActive ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      {stepIdx !== workflowSteps.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-2 ${isCompleted ? 'bg-indigo-600' : 'bg-gray-300'}`} />
                      )}
                    </div>
                    {/* use a screen-reader only label so the text is not visibly duplicated */}
                    <span className="sr-only">{step.name}</span>
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>

        {/* Quick Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Workflow buttons based on current state */}
              {(alert.state === 'open' || alert.state === 'new') && (
                <>
                  <button
                    onClick={() => acknowledgeMutation.mutate()}
                    disabled={acknowledgeMutation.isPending}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    <ClockIcon className="h-4 w-4 mr-2" />
                    Acknowledge
                  </button>
                  <button
                    onClick={() => updateStateMutation.mutate('in_progress')}
                    disabled={updateStateMutation.isPending}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Start Work
                  </button>
                </>
              )}
              
              {alert.state === 'acknowledged' && (
                <button
                  onClick={() => updateStateMutation.mutate('in_progress')}
                  disabled={updateStateMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Start Work
                </button>
              )}
              
              {alert.state === 'in_progress' && (
                <button
                  onClick={() => updateStateMutation.mutate('resolved')}
                  disabled={updateStateMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Resolve
                </button>
              )}
              
              {(alert.state === 'resolved' || alert.state === 'auto_resolved') && (
                <button
                  onClick={() => updateStateMutation.mutate('closed')}
                  disabled={updateStateMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <XMarkIcon className="h-4 w-4 mr-2" />
                  Close
                </button>
              )}
              
              {/* Create Case button - available for any non-resolved, non-auto-resolved and non-closed alert that doesn't have a related case */}
              {alert.state !== 'closed' && alert.state !== 'resolved' && alert.state !== 'auto_resolved' && !alert.relatedCaseId && (
                <button
                  onClick={() => createCaseFromAlert()}
                  disabled={createCaseMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-purple-300 text-sm font-medium rounded-md text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {createCaseMutation.isPending ? 'Creating Case...' : 'Create Case'}
                </button>
              )}
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${stateColors[alert.state as keyof typeof stateColors] || 'bg-gray-100 text-gray-800'}`}>
              {(() => {
                switch (alert.state) {
                  case 'open': return 'Open';
                  case 'in_progress': return 'In-Progress';
                  case 'resolved': return 'Resolved';
                  case 'auto_resolved': return 'Auto-Resolved';
                  case 'closed': return 'Closed';
                  case 'new': return 'New';
                  case 'acknowledged': return 'Acknowledged';
                  case 'assigned': return 'Assigned';
                  default: return alert.state?.charAt(0).toUpperCase() + alert.state?.slice(1).replace('_', ' ') || 'Unknown';
                }
              })()}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('details')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'details'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('attachments')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'attachments'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Attachments
          </button>
        </nav>
      </div>

      {activeTab === 'details' && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Description</h2>
            </div>
            <div className="px-6 py-4">
              {isEditing ? (
                <textarea
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Detailed description of the alert"
                />
              ) : (
                <RichTextDisplay content={alert.description || ''} className="text-gray-700" />
              )}
            </div>
          </div>

          {/* Technical Details */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Technical Details</h2>
            </div>
            <div className="px-6 py-4">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Source</dt>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.source || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, source: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      placeholder="Alert source"
                    />
                  ) : (
                    <dd className="mt-1 text-sm text-gray-900">{alert.source}</dd>
                  )}
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Source Service</dt>
                  <dd className="mt-1 text-sm text-gray-900">{alert.sourceService || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Source Sub Service</dt>
                  <dd className="mt-1 text-sm text-gray-900">{alert.sourceSubService || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Title</dt>
                  <dd className="mt-1 text-sm text-gray-900">{alert.title || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1 text-sm text-gray-900">{alert.status || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Type</dt>
                  <dd className="mt-1 text-sm text-gray-900">{alert.type || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Service Type</dt>
                  <dd className="mt-1 text-sm text-gray-900">{alert.serviceType || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Node/Host</dt>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.node || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, node: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      placeholder="Node or host"
                    />
                  ) : (
                    <dd className="mt-1 text-sm text-gray-900">{alert.node || '-'}</dd>
                  )}
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Metric/Threshold</dt>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.metric || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, metric: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      placeholder="Metric or threshold"
                    />
                  ) : (
                    <dd className="mt-1 text-sm text-gray-900">{alert.metric || '-'}</dd>
                  )}
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Category</dt>
                  <select
                    value={(() => {
                      // Map backend values to display values
                      switch (alert.category) {
                        case 'problem_incident': return 'Problem/Incident';
                        case 'labtech': return 'LabTech';
                        default: return alert.category || '';
                      }
                    })()}
                    disabled
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-50 text-gray-500 cursor-not-allowed text-sm"
                  >
                    <option value="Problem/Incident">Problem/Incident</option>
                    <option value="LabTech">LabTech</option>
                  </select>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">State</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${stateColors[alert.state as keyof typeof stateColors] || 'bg-gray-100 text-gray-800'}`}>
                      {(() => {
                        switch (alert.state) {
                          case 'open': return 'Open';
                          case 'in_progress': return 'In-Progress';
                          case 'resolved': return 'Resolved';
                          case 'auto_resolved': return 'Auto-Resolved';
                          case 'closed': return 'Closed';
                          default: return alert.state?.charAt(0).toUpperCase() + alert.state?.slice(1).replace('_', ' ') || '-';
                        }
                      })()}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Resource</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {alert.resourceId ? (
                      resourceData ? (
                        <Link 
                          to={`/resources/${alert.resourceId}`}
                          className="text-indigo-600 hover:text-indigo-800 underline"
                        >
                          {resourceData.name} ({resourceData.resourceType})
                        </Link>
                      ) : (
                        <span className="text-gray-500">Loading resource...</span>
                      )
                    ) : (
                      '-'
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Occurrence Count</dt>
                  <dd className="mt-1 text-sm text-gray-900">{alert.count} times</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">First Seen</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDateTime(alert.firstSeen)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Seen</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDateTime(alert.lastSeen)}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Metadata */}
          {alert.metadata && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Metadata</h2>
              </div>
              <div className="px-6 py-4">
                <JsonViewer data={alert.metadata} />
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Alert Info */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Alert Information</h2>
            </div>
            <div className="px-6 py-4">
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Alert Number</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{alert.number}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Priority
                    {isEditing && (
                      <span className="ml-2 text-xs text-gray-400">(Auto-calculated from Severity)</span>
                    )}
                  </dt>
                  <dd className="mt-1">
                    <div className="flex items-center">
                      {(() => {
                        const priorityStringToNumber: Record<string, number> = {
                          'CRITICAL': 1,
                          'HIGH': 2,
                          'NORMAL': 3,
                          'MEDIUM': 4,
                          'LOW': 5
                        };
                        const priorityNumberToString: Record<number, string> = {
                          1: 'CRITICAL',
                          2: 'HIGH',
                          3: 'NORMAL',
                          4: 'MEDIUM',
                          5: 'LOW'
                        };
                        const priorityValue = typeof alert.priority === 'string'
                          ? priorityStringToNumber[alert.priority] || 3
                          : alert.priority;
                        const priorityLabel = typeof alert.priority === 'string'
                          ? alert.priority
                          : priorityNumberToString[alert.priority] || 'NORMAL';
                        // Invert the logic: Critical (1) = 4 stars, High (2) = 3 stars, Low (5) = 1 star
                        const starsToShow = Math.max(1, 5 - priorityValue);

                        return (
                          <>
                            {[...Array(4)].map((_, i) => (
                              <StarSolidIcon
                                key={i}
                                className={`h-4 w-4 ${i < starsToShow ? 'text-yellow-400' : 'text-gray-300'}`}
                              />
                            ))}
                            <span className="ml-2 text-xs text-gray-600">
                              {priorityLabel}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDateTime(alert.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDateTime(alert.updatedAt)}</dd>
                </div>
              </dl>
            </div>
          </div>

        </div>
      </div>
      )}

      {/* Attachments Tab */}
      {activeTab === 'attachments' && (
        <div className="bg-white shadow rounded-lg p-6">
          <AttachmentTab
            entityType="alert"
            entityId={id!}
            canUpload={true}
            canDelete={true}
            ticketState={alert?.state}
          />
        </div>
      )}

      {/* Record Time Modal */}
      {alert && alert.customer && alert.engagement && (
        <RecordTimeModal
          isOpen={recordTimeModalOpen}
          onClose={() => setRecordTimeModalOpen(false)}
          onSuccess={() => {
            // Optionally refresh the alert data after recording time
            queryClient.invalidateQueries({ queryKey: ['alert', id] });
          }}
          ticketNumber={alert.alertNumber || ''}
          customerName={alert.customer.accountName || alert.customer.name}
          customerId={alert.customerId || alert.customer.id}
          engagementName={alert.engagement.name}
          engagementId={alert.engagementId || alert.engagement.id}
          groupName=""
          groupId=""
        />
      )}
    </div>
  );
};

export default AlertDetail;
